import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3 } from 'lucide-react';
import { PageLayout } from '@/components/ui/PageLayout';
import { LoadingState } from '@/components/AsyncStates';
import WorkloadTable from './WorkloadTable';
import { useRefereeWorkload } from './useRefereeWorkload';

function getDefaultStartDate(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
}

function getDefaultEndDate(): string {
  return new Date().toISOString().split('T')[0];
}

const RefereeWorkload: React.FC = () => {
  const { t } = useTranslation('tournament');

  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState(getDefaultEndDate);

  const dateRange = useMemo(() => ({ start: startDate, end: endDate }), [startDate, endDate]);
  const { data, loading, error, refetch } = useRefereeWorkload(dateRange);

  const inputClasses =
    'w-full px-3 py-2 text-sm bg-background border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors outline-none';

  if (loading) {
    return (
      <PageLayout
        title={t('Referee Workload')}
        description={t('View referee workload distribution and match assignments')}
        icon={BarChart3}
        accentColor="amber"
      >
        <LoadingState title={t('Loading data...')} />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t('Referee Workload')}
      description={t('View referee workload distribution and match assignments')}
      icon={BarChart3}
      accentColor="amber"
    >
      {/* Filters */}
      <section
        className="bg-card border border-border rounded-lg p-6 mb-6"
        data-testid="workload-filters"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('Filters')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('Start Date')}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={inputClasses}
              data-testid="filter-start-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('End Date')}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={inputClasses}
              data-testid="filter-end-date"
            />
          </div>
        </div>
      </section>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3" data-testid="workload-error">
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          <button
            onClick={refetch}
            className="text-sm font-medium text-red-700 dark:text-red-400 hover:underline"
            data-testid="workload-retry"
          >
            {t('Try Again')}
          </button>
        </div>
      )}

      {/* Table */}
      <WorkloadTable data={data} />
    </PageLayout>
  );
};

export default RefereeWorkload;
